import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FaTshirt,
  FaUserTie,
  FaCut,
  FaBox,
  FaBullseye,
  FaEye,
  FaGem,
  FaAward,
  FaUsers,
  FaCog,
  FaShieldAlt,
  FaMapMarkerAlt,
  FaEnvelope,
  FaArrowRight,
  FaCheck,
  FaPhone,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { submitContactForm } from "../store/slices/contactSlice";
import { fetchProducts } from "../store/slices/productSlice";
import { fetchSliders } from "../store/slices/sliderSlice";

const FALLBACK_SLIDES = [
  { imageUrl: "/images/slider1.png", title: "", subtitle: "", link: "" },
  { imageUrl: "/images/slider2.png", title: "", subtitle: "", link: "" },
  { imageUrl: "/images/slider3.png", title: "", subtitle: "", link: "" },
];

const services = [
  {
    icon: FaTshirt,
    title: "T-Shirt Manufacturing",
    desc: "Stylish and comfortable t-shirts with premium fabric quality and custom prints.",
    color: "bg-blue-50 text-blue-700",
  },
  {
    icon: FaUserTie,
    title: "Formal Wear",
    desc: "Professional men formal garments designed with precision and elegance.",
    color: "bg-indigo-50 text-indigo-700",
  },
  {
    icon: FaCut,
    title: "Custom Stitching",
    desc: "Customized garment stitching for bulk, corporate, and special orders.",
    color: "bg-sky-50 text-sky-700",
  },
  {
    icon: FaBox,
    title: "Bulk Supply",
    desc: "Large-scale production and supply for fashion businesses across India.",
    color: "bg-teal-50 text-teal-700",
  },
];

const statsBar = [
  { number: "10+", label: "Years Experience" },
  { number: "500+", label: "Happy Clients" },
  { number: "100K+", label: "Garments Produced" },
  { number: "1500+", label: "Skilled Workforce" },
];

const showcaseProducts = [
  {
    img: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?q=80&w=1000&auto=format&fit=crop",
    title: "Premium T-Shirts",
    desc: "Comfortable and stylish casual wear collection.",
    category: "Casual",
  },
  {
    img: "https://images.unsplash.com/photo-1603252109303-2751441dd157?q=80&w=1000&auto=format&fit=crop",
    title: "Formal Shirts",
    desc: "Elegant office and corporate fashion garments.",
    category: "Formal",
  },
  {
    img: "/images/denim.png",
    title: "Denim Collection",
    desc: "Durable and trendy denim wear manufacturing.",
    category: "Denim",
  },
  {
    img: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=1000&auto=format&fit=crop",
    title: "Blazers & Jackets",
    desc: "Modern and premium outerwear collections.",
    category: "Outerwear",
  },
];

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sectionSlide, setSectionSlide] = useState(0);

  const dispatch = useDispatch();
  const { formSubmitting, formSuccess, formError } = useSelector(
    (state) => state.contact,
  );
  const { products } = useSelector((state) => state.products);
  const { sliders: dbSliders } = useSelector((state) => state.slider);

  // Top hero slider — always static
  const slides = FALLBACK_SLIDES;

  // Bottom section slider — dynamic from DB, fallback to static if empty
  const sectionImages =
    dbSliders && dbSliders.length > 0
      ? dbSliders.map((s) => ({ src: s.imageUrl, caption: s.title || "" }))
      : [
          { src: "/images/slider1.png", caption: "Premium Garment Manufacturing" },
          { src: "/images/slider2.png", caption: "Quality Craftsmanship Since 2006" },
          { src: "/images/slider3.png", caption: "Bulk Orders & Custom Stitching" },
        ];

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  useEffect(() => {
    if (currentSlide >= slides.length) setCurrentSlide(0);
  }, [slides.length]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [slides.length]);

  useEffect(() => {
    if (sectionImages.length === 0) return;
    if (sectionSlide >= sectionImages.length) setSectionSlide(0);
    const interval = setInterval(() => {
      setSectionSlide((prev) => (prev + 1) % sectionImages.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [sectionImages.length]);

  useEffect(() => {
    dispatch(fetchProducts());
    dispatch(fetchSliders());
  }, [dispatch]);

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData();
    Object.entries(formData).forEach(([k, v]) => fd.append(k, v));
    dispatch(submitContactForm(fd));
  };

  const prevSlide = () =>
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);

  return (
    <>
      {/* ── Hero Slider ── */}
      <section className="relative overflow-hidden h-[580px]">
        {slides.map((slide, i) => (
          <div
            key={slide._id || i}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              i === currentSlide ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={slide.imageUrl}
              alt={slide.title || "Slide"}
              className="w-full h-full object-cover"
            />
          </div>
        ))}

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />

        {/* Content */}
        <div className="absolute inset-0 flex items-center z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full">
            <div className="max-w-2xl text-white">
              <span className="inline-block bg-blue-700 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-6">
                Premium Garment Manufacturing
              </span>
              {slides[currentSlide]?.title ? (
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-white">
                  {slides[currentSlide].title}
                </h1>
              ) : (
                <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold leading-tight text-white">
                  OM Maruti <br />
                  <span className="text-blue-400">Creation</span>
                </h1>
              )}
              {slides[currentSlide]?.subtitle ? (
                <p className="mt-5 text-base md:text-lg text-gray-200 leading-relaxed max-w-xl">
                  {slides[currentSlide].subtitle}
                </p>
              ) : (
                <p className="mt-5 text-base md:text-lg text-gray-200 leading-relaxed max-w-xl">
                  Trusted men garment manufacturing company since 2006,
                  Bangalore — delivering premium quality apparel solutions at
                  scale.
                </p>
              )}
              <div className="mt-8 flex gap-4 flex-wrap">
                {slides[currentSlide]?.link ? (
                  <Link
                    to={slides[currentSlide].link}
                    className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-semibold px-6 py-3 rounded-full transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    Explore More <FaArrowRight className="text-sm" />
                  </Link>
                ) : (
                  <Link
                    to="/products"
                    className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-semibold px-6 py-3 rounded-full transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    Explore Products <FaArrowRight className="text-sm" />
                  </Link>
                )}
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 border-2 border-white text-white hover:bg-white hover:text-blue-700 font-semibold px-6 py-3 rounded-full transition-all duration-200"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Arrow buttons */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 text-white w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-sm transition"
          aria-label="Previous slide"
        >
          <FaChevronLeft />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 text-white w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-sm transition"
          aria-label="Next slide"
        >
          <FaChevronRight />
        </button>

        {/* Dots */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3 z-20">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={`transition-all duration-300 rounded-full ${
                i === currentSlide ? "w-8 h-3 bg-white" : "w-3 h-3 bg-white/50"
              }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* ── Stats Ticker Bar ── */}
      <section className="bg-blue-700 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-around gap-8">
            {statsBar.map((stat, i) => (
              <React.Fragment key={i}>
                <div className="text-center text-white">
                  <p className="text-4xl font-bold">{stat.number}</p>
                  <p className="text-blue-200 text-sm mt-1 font-medium">
                    {stat.label}
                  </p>
                </div>
                {i < statsBar.length - 1 && (
                  <div className="hidden sm:block w-px h-12 bg-white/30" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ── About Section ── */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">
              About Us
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
              OM Maruti Creation
            </h2>
            <div className="flex items-center gap-4 mt-6">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-sm font-semibold text-blue-700 tracking-widest uppercase whitespace-nowrap">
                Since 2006
              </span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>
            <p className="mt-6 text-gray-600 text-lg leading-relaxed">
              OM Maruthi Creations is a leading men garment manufacturing
              company established in 2006 with a strong reputation for quality,
              innovation, and reliable production. Located in Peenya 2nd Stage,
              Bangalore, we specialize in premium men's apparel including formal
              wear, casual wear, uniforms, and customized garment solutions.
            </p>
            <div className="mt-10 flex items-center gap-6 bg-blue-50 rounded-2xl p-6">
              <div className="bg-blue-700 h-16 w-16 rounded-full flex items-center justify-center text-white text-2xl shadow-lg flex-shrink-0">
                <FaUsers />
              </div>
              <div>
                <h4 className="text-2xl font-bold text-gray-900">
                  1,500 – 1,800
                </h4>
                <p className="text-gray-600 mt-1">Total Factory Labours</p>
              </div>
            </div>
            <div className="mt-8">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-semibold px-6 py-3 rounded-full transition-all duration-200 shadow-sm hover:shadow-md"
              >
                Learn More <FaArrowRight className="text-sm" />
              </Link>
            </div>
          </div>

          {/* Right */}
          <div className="relative">
            <div className="rounded-3xl overflow-hidden h-[480px]">
              <img
                src="/images/about.png"
                alt="About OM Maruthi Creations"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-blue-700 text-white rounded-2xl p-5 shadow-xl">
              <p className="text-3xl font-bold">1500+</p>
              <p className="text-blue-200 text-sm mt-1">Skilled Workers</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Services Section ── */}
      <section className="py-20 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">
              What We Offer
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
              Our Services
            </h2>
            <p className="mt-4 text-gray-500 text-lg">
              Complete garment manufacturing solutions for fashion brands,
              wholesalers, and retailers.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, i) => {
              const Icon = service.icon;
              return (
                <div
                  key={i}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-shadow duration-300 p-8 flex flex-col hover:border-blue-200 hover:-translate-y-1"
                >
                  <div
                    className={`h-14 w-14 rounded-2xl flex items-center justify-center text-2xl mb-6 ${service.color}`}
                  >
                    <Icon />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed flex-1">
                    {service.desc}
                  </p>
                  <Link
                    to="/service"
                    className="mt-6 text-blue-700 font-semibold text-sm inline-flex items-center gap-1 hover:gap-2 transition-all"
                  >
                    Learn More <FaArrowRight className="text-xs" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Products Section ── */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-14 gap-4">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">
                Collections
              </span>
              <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
                Our Products
              </h2>
            </div>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 text-blue-700 font-semibold hover:underline whitespace-nowrap"
            >
              View All <FaArrowRight className="text-sm" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {showcaseProducts.map((product, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden group hover:-translate-y-1"
              >
                <div className="relative h-60 overflow-hidden">
                  <img
                    src={product.img}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                    alt={product.title}
                  />
                  <span className="absolute top-3 left-3 bg-blue-700 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    {product.category}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-gray-900">
                    {product.title}
                  </h3>
                  <p className="text-gray-500 text-sm mt-2">{product.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Mission / Vision / Values ── */}
      <section className="py-20 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">
              Who We Are
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
              Our Core Principles
            </h2>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Mission — blue */}
            <div className="bg-blue-700 text-white p-10 rounded-2xl shadow-sm">
              <div className="h-14 w-14 bg-white/20 rounded-2xl flex items-center justify-center text-2xl mb-6">
                <FaBullseye />
              </div>
              <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
              <p className="text-blue-100 leading-relaxed">
                To deliver premium-quality men garments with innovation,
                reliability, and customer satisfaction while maintaining the
                highest manufacturing standards.
              </p>
            </div>
            {/* Vision */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10">
              <div className="h-14 w-14 bg-blue-50 rounded-2xl flex items-center justify-center text-2xl text-blue-700 mb-6">
                <FaEye />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Our Vision
              </h3>
              <p className="text-gray-500 leading-relaxed">
                To become a globally recognized garment manufacturing brand
                known for quality, trust, innovation, and sustainable production
                practices.
              </p>
            </div>
            {/* Values */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10">
              <div className="h-14 w-14 bg-blue-50 rounded-2xl flex items-center justify-center text-2xl text-blue-700 mb-6">
                <FaGem />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Core Values
              </h3>
              <p className="text-gray-500 leading-relaxed">
                Integrity, quality craftsmanship, customer satisfaction,
                teamwork, innovation, and timely delivery are the pillars of our
                company.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Contact CTA Section ── */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left: Info */}
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">
                Get In Touch
              </span>
              <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
                Contact Us
              </h2>
              <p className="mt-5 text-gray-500 text-lg leading-relaxed">
                Reach out to OM Maruti Creation for premium garment
                manufacturing services and bulk apparel production.
              </p>
              <div className="mt-10 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-700 text-white h-12 w-12 rounded-xl flex items-center justify-center text-lg flex-shrink-0">
                    <FaMapMarkerAlt />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Office Address</h4>
                    <p className="text-gray-500 mt-1">
                      Peenya 2nd Stage, Bangalore - 560058
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-blue-700 text-white h-12 w-12 rounded-xl flex items-center justify-center text-lg flex-shrink-0">
                    <FaEnvelope />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Email Address</h4>
                    <p className="text-gray-500 mt-1">
                      info@ommaruticreation.com
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-blue-700 text-white h-12 w-12 rounded-xl flex items-center justify-center text-lg flex-shrink-0">
                    <FaPhone />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Phone</h4>
                    <p className="text-gray-500 mt-1">+91 0987654321</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
              {formSuccess && (
                <div className="mb-5 p-4 bg-green-50 text-green-700 rounded-xl border border-green-200 text-sm">
                  Message sent successfully! We'll be in touch soon.
                </div>
              )}
              {formError && (
                <div className="mb-5 p-4 bg-red-50 text-red-700 rounded-xl border border-red-200 text-sm">
                  {formError}
                </div>
              )}
              <form className="space-y-4" onSubmit={handleFormSubmit}>
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleFormChange}
                  className="w-full border border-gray-200 rounded-xl px-5 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={handleFormChange}
                  className="w-full border border-gray-200 rounded-xl px-5 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                />
                <input
                  type="text"
                  name="subject"
                  placeholder="Subject"
                  value={formData.subject}
                  onChange={handleFormChange}
                  className="w-full border border-gray-200 rounded-xl px-5 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                />
                <textarea
                  rows="5"
                  name="message"
                  placeholder="Your Message"
                  value={formData.message}
                  onChange={handleFormChange}
                  className="w-full border border-gray-200 rounded-xl px-5 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
                />
                <button
                  type="submit"
                  disabled={formSubmitting}
                  className="w-full inline-flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-semibold px-6 py-3.5 rounded-full transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-60"
                >
                  {formSubmitting ? "Sending…" : "Send Message"}
                </button>
              </form>
            </div>
          </div>

          {/* Map — commented out
          <div className="mt-16">
            <iframe
              className="w-full h-80 rounded-2xl shadow-sm border border-gray-100"
              src="https://maps.google.com/maps?q=Peenya%202nd%20Stage%20Bangalore&t=&z=13&ie=UTF8&iwloc=&output=embed"
              allowFullScreen=""
              loading="lazy"
              title="OM Maruti Creation Location"
            />
          </div>
          */}

          {/* ── Section Image Slider ── */}
          <div className="mt-16 relative rounded-2xl overflow-hidden shadow-sm border border-gray-100 h-80 group">
            {sectionImages.map((img, i) => (
              <div
                key={i}
                className={`absolute inset-0 transition-opacity duration-700 ${
                  i === sectionSlide ? "opacity-100" : "opacity-0"
                }`}
              >
                <img
                  src={img.src}
                  alt={img.caption}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/30" />
                <p className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white font-semibold text-sm tracking-wide bg-black/40 px-4 py-1.5 rounded-full whitespace-nowrap">
                  {img.caption}
                </p>
              </div>
            ))}

            {/* Arrows */}
            <button
              onClick={() =>
                setSectionSlide(
                  (p) => (p - 1 + sectionImages.length) % sectionImages.length,
                )
              }
              className="absolute left-3 top-1/2 -translate-y-1/2 z-10 bg-white/20 hover:bg-white/50 text-white w-9 h-9 rounded-full flex items-center justify-center backdrop-blur-sm transition opacity-0 group-hover:opacity-100"
              aria-label="Previous"
            >
              <FaChevronLeft />
            </button>
            <button
              onClick={() =>
                setSectionSlide((p) => (p + 1) % sectionImages.length)
              }
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10 bg-white/20 hover:bg-white/50 text-white w-9 h-9 rounded-full flex items-center justify-center backdrop-blur-sm transition opacity-0 group-hover:opacity-100"
              aria-label="Next"
            >
              <FaChevronRight />
            </button>

            {/* Dots */}
            <div className="absolute bottom-3 right-4 flex gap-2 z-10">
              {sectionImages.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setSectionSlide(i)}
                  className={`rounded-full transition-all duration-300 ${
                    i === sectionSlide
                      ? "w-6 h-2 bg-white"
                      : "w-2 h-2 bg-white/50"
                  }`}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
